import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface HolographicRingsProps {
  scrollProgress?: number;
  mousePosition?: THREE.Vector2;
}

const HolographicRings: React.FC<HolographicRingsProps> = ({ scrollProgress = 0, mousePosition }) => {
  const groupRef = useRef<THREE.Group>(null);
  const ringsRef = useRef<THREE.Mesh[]>([]);
  const ringCount = 12;

  const ringData = useMemo(() => {
    return Array.from({ length: ringCount }, (_, i) => ({
      radius: 1 + i * 0.5,
      segments: 64,
      speed: 0.5 + i * 0.1,
      offset: i * 0.5,
      color: new THREE.Color().setHSL((i / ringCount) * 0.3 + 0.5, 1, 0.5)
    }));
  }, []);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;

    const time = clock.getElapsedTime();

    ringsRef.current.forEach((ring, i) => {
      if (!ring) return;

      const data = ringData[i];

      // Rotate each ring at different speeds
      ring.rotation.z = time * data.speed + data.offset;

      // Wobble based on scroll
      ring.rotation.x = Math.sin(scrollProgress * Math.PI + i * 0.3) * 0.5;
      ring.rotation.y = scrollProgress * Math.PI * 2 + i * 0.2;

      // Scale pulse
      const scale = 1 + Math.sin(time * 0.5 + i * 0.3) * 0.1;
      ring.scale.setScalar(scale);

      // Opacity pulse
      const material = ring.material as THREE.MeshStandardMaterial;
      material.opacity = 0.3 + Math.sin(time + i) * 0.2;
    });

    // Tilt whole structure based on scroll
    groupRef.current.rotation.x = scrollProgress * Math.PI * 0.5;

    // Mouse interaction
    if (mousePosition) {
      groupRef.current.rotation.y = mousePosition.x * Math.PI * 0.3;
      groupRef.current.rotation.z = -mousePosition.y * 0.2;
    }
  });

  return (
    <group ref={groupRef} position={[0, 0, -5]}>
      {ringData.map((data, i) => (
        <mesh
          key={i}
          ref={(el) => {
            if (el) ringsRef.current[i] = el;
          }}
        >
          <torusGeometry args={[data.radius, 0.03, 16, data.segments]} />
          <meshStandardMaterial
            color={data.color}
            emissive={data.color}
            emissiveIntensity={0.1}
            transparent
            opacity={0.08}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
};

export default HolographicRings;
